/**
 * The anchor controls that are worth having in more than one place.
 *
 * There are two anchor screens: the Alarms sheet and Menu → Safety → Anchor
 * watch. That is fine, they suit different moments, but it means anything added
 * to one and not the other is invisible to whoever happens to be standing in
 * the other. Which is exactly what happened: the compass bearing and the
 * boundary editor went into the sheet only, and Edd works from the menu, so as
 * far as he could tell they had never shipped.
 *
 * So they live here once and are rendered by both.
 */
import { useEffect, useRef, useState } from 'react';
import { useAnchorWatchStore } from '@/store/anchorWatch';
import { useChartPlotterStore } from '@/store';
import { useShellStore } from '@/store/shell';
import { useBearingDisplayStore } from '@/store/bearingDisplay';
import { swingRadiusNm, anchorFromBearing } from '@/safety';
import { zoneIsPlainCircle, DEFAULT_ZONE_POINTS } from '@/safety/anchorZone';
import { horizontalRunM } from '@/ownship/hullOutline';
import { anchorPositionNow } from '@/ownship/anchorDrop';
import { requestCompassPermission, watchCompass } from '@/ownship/compass';
import { haptic } from '@/ui/haptics';

/**
 * The three ways to say where the anchor is.
 *
 * "Drop here" on its own only serves one moment, the hook going in with the app
 * already open in your hand, and that is the rarest of the three. Most watches
 * get started after the fact, with the boat already lying back on its rode, and
 * dropping the anchor at the boat then records it a boat length or more astern
 * of where it really is. So there are two more (fb-msdnj1cy-1):
 *
 *   Drop here          the hook is going in right now, at the bow.
 *   Range and bearing  we can see where the chain goes in. Point the phone at
 *                      it, or type the bearing, and the rode gives the distance.
 *   Place on chart     we know the spot on the chart. Tap it.
 *
 * They are a chooser rather than three panels stacked up, because only one of
 * them is ever the answer and a stack of anchor controls at two in the morning
 * is how the wrong button gets pressed.
 */
export function AnchorPlacement() {
  const anchor = useAnchorWatchStore((s) => s.anchor);
  const dropAnchor = useAnchorWatchStore((s) => s.dropAnchor);
  const boatPos = useChartPlotterStore((s) => s.ownship.position);
  const [how, setHow] = useState<'bearing' | null>(null);

  return (
    <>
      <label className="acp-fieldlabel">{anchor ? 'Move the anchor' : 'Where is the anchor?'}</label>
      <div className="picks compact">
        <button
          type="button"
          className="acp-btn"
          disabled={!boatPos}
          onClick={() => {
            const at = boatPos ? anchorPositionNow(boatPos) ?? boatPos : null;
            if (!at) return;
            haptic('commit');
            setHow(null);
            dropAnchor(at);
            useShellStore.getState().showToast(anchor ? 'Anchor moved to the bow' : 'Anchor dropped at the bow');
          }}
        >
          Drop here
        </button>
        <button
          type="button"
          className={`acp-btn${how === 'bearing' ? ' on' : ''}`}
          onClick={() => { haptic('tick'); setHow(how === 'bearing' ? null : 'bearing'); }}
        >
          Range and bearing
        </button>
        <button
          type="button"
          className="acp-btn"
          onClick={() => {
            haptic('tick');
            setHow(null);
            // Arming closes the sheet, so the hint banner on the chart is what
            // tells them what to do next. See AnchorArmHint in Shell.
            useShellStore.getState().armAnchorPick();
            useShellStore.getState().showToast('Tap the chart where the anchor went in');
          }}
        >
          Place on chart
        </button>
      </div>
      {how === 'bearing' ? (
        <AnchorByBearing />
      ) : (
        <p className="acp-menu-note">
          Drop here puts it at the bow this second, for the hook going in as we watch. The other two are
          for an anchor that is already down: by the time most of us start a watch the boat has dropped
          back on the rode, so the bow is a boat length or more from the anchor.
        </p>
      )}
    </>
  );
}

/**
 * Place or move the anchor by a bearing and a distance, with the phone's own
 * compass to take the bearing and the rode to work out the distance.
 *
 * This is how an anchor that is ALREADY down gets recorded: stand at the bow,
 * point the phone at where the chain goes in, say how much rode is out.
 */
export function AnchorByBearing() {
  const anchor = useAnchorWatchStore((s) => s.anchor);
  const dropAnchor = useAnchorWatchStore((s) => s.dropAnchor);
  const scopeM = useAnchorWatchStore((s) => s.scopeM);
  const depthM = useAnchorWatchStore((s) => s.depthM);
  const boatPos = useChartPlotterStore((s) => s.ownship.position);
  const variationDeg = useBearingDisplayStore((s) => s.effectiveVariationDeg);

  const [brg, setBrg] = useState('0');
  const [dist, setDist] = useState('20');
  const [live, setLive] = useState<number | null>(null);
  const stopRef = useRef<(() => void) | null>(null);

  const stop = () => {
    stopRef.current?.();
    stopRef.current = null;
    setLive(null);
  };
  useEffect(() => stop, []);

  if (!boatPos) return null;

  const start = async () => {
    if (stopRef.current) {
      stop();
      return;
    }
    // Straight off the tap: iOS refuses the permission otherwise.
    if (!(await requestCompassPermission())) {
      useShellStore.getState().showToast('No compass permission — type the bearing instead');
      return;
    }
    haptic('tick');
    stopRef.current = watchCompass((r) => {
      // The phone reads magnetic; everything downstream is true.
      setLive((((r.magneticDeg + variationDeg) % 360) + 360) % 360);
    });
  };

  const place = () => {
    const b = parseFloat(brg);
    const d = parseFloat(dist);
    if (!Number.isFinite(b) || !Number.isFinite(d) || d < 0) return;
    haptic('commit');
    // From the BOW: the rode runs from the bow roller, so a bearing measured
    // from anywhere else lands the anchor out by however far apart they are.
    const from = anchorPositionNow(boatPos) ?? boatPos;
    dropAnchor(anchorFromBearing(from, (((b % 360) + 360) % 360), d));
    useShellStore.getState().showToast(anchor ? 'Anchor moved' : 'Anchor placed — watch armed');
  };

  return (
    <>
      <label className="acp-fieldlabel">Bearing and distance from the bow</label>
      <div className="picks compact">
        <input
          className="acp-text-input"
          value={brg}
          onChange={(e) => setBrg(e.target.value)}
          placeholder="Bearing °T"
          inputMode="decimal"
          aria-label="Bearing to the anchor, degrees true"
        />
        <input
          className="acp-text-input"
          value={dist}
          onChange={(e) => setDist(e.target.value)}
          placeholder="Distance m"
          inputMode="decimal"
          aria-label="Distance to the anchor in metres"
        />
      </div>
      <div className="picks compact">
        <button type="button" className="acp-btn" onClick={() => void start()}>
          {stopRef.current ? 'Stop compass' : 'Point the phone at it'}
        </button>
        <button
          type="button"
          className="acp-btn"
          onClick={() => {
            haptic('tick');
            setDist(horizontalRunM(scopeM, depthM).toFixed(0));
          }}
        >
          Distance from rode
        </button>
      </div>
      {live != null && (
        <button type="button" className="acp-btn on" onClick={() => { haptic('commit'); setBrg(live.toFixed(0)); stop(); }}>
          Hold steady, tap to take {live.toFixed(0)}&deg;T
        </button>
      )}
      <p className="acp-menu-note">
        Point the top of the phone at where the chain goes in and hold it level, well clear of the engine
        and anything steel, exactly as we would a hand-bearing compass. The phone reads magnetic and we
        apply the variation. Distance from rode works the run along the surface out of the scope and the
        depth, rather than the chain paid out, which would put the anchor further away than it is.
      </p>
      <button type="button" className="acp-btn on" onClick={place}>
        {anchor ? 'Move the anchor there' : 'Place the anchor there'}
      </button>
    </>
  );
}

/**
 * Turn the swing circle into a boundary we can shape.
 *
 * A single radius has to be set for the worst bearing, so it either cries wolf
 * all night on the open side or says nothing until we are on the rocks. Seeding
 * from the circle rather than drawing from scratch matters: the circle is
 * already right nearly everywhere, and the job is pulling it in on the one side
 * that has something in it.
 */
export function AnchorBoundaryShape() {
  const anchor = useAnchorWatchStore((s) => s.anchor);
  const zone = useAnchorWatchStore((s) => s.zone);
  const mode = useAnchorWatchStore((s) => s.mode);
  const setMode = useAnchorWatchStore((s) => s.setMode);
  const seed = useAnchorWatchStore((s) => s.seedZoneFromCircle);
  const clearZone = useAnchorWatchStore((s) => s.clearZone);
  const scopeM = useAnchorWatchStore((s) => s.scopeM);
  const depthM = useAnchorWatchStore((s) => s.depthM);
  const bowHeightM = useAnchorWatchStore((s) => s.bowHeightM);

  if (!anchor) return null;

  return (
    <>
      <label className="acp-fieldlabel">Alarm boundary</label>
      <div className="picks compact">
        <button
          type="button"
          className={mode === 'radius' ? 'on' : ''}
          onClick={() => { haptic('tick'); setMode('radius'); }}
        >
          <b>Swing circle</b>
        </button>
        <button
          type="button"
          className={mode === 'zone' ? 'on' : ''}
          onClick={() => {
            haptic('tick');
            // Switching to a shaped boundary with nothing drawn yet would leave
            // the alarm with no area to test against, so seed it from the circle
            // in the same move. That is the usual route in anyway.
            //
            // A stored boundary that is still a plain circle gets re-seeded too.
            // The boundary is persisted so an overnight watch survives the app
            // being killed, which also means one seeded under an older version
            // keeps its old point count for ever, and that count has come down
            // twice now (fb-msdph9hi-1). A boundary that has actually been
            // shaped is left alone: pulling it in off the rocks is work, and
            // quietly undoing it would be a rotten trick.
            const stale =
              zone.length > DEFAULT_ZONE_POINTS && zoneIsPlainCircle(zone, anchor);
            if (zone.length < 3 || stale) {
              seed(anchor, swingRadiusNm({ scopeM, depthM, bowHeightM }));
              useShellStore.getState().showToast('Boundary from the swing circle — drag a point to shape it');
            } else {
              setMode('zone');
            }
          }}
        >
          <b>Shaped</b>
        </button>
      </div>
      {mode === 'radius' ? (
        <p className="acp-menu-note">
          The circle sizes itself from the rode and the depth. Good wherever there is room to swing.
        </p>
      ) : (
        <>
          <p className="acp-menu-note">
            On the chart: drag any point in or out, tap a small hollow handle between two points to add
            one, and hold a point to remove it. Pull it in on the side with the rocks and leave the open
            side alone.
          </p>
          <div className="picks compact">
            <button
              type="button"
              className="acp-btn"
              onClick={() => {
                haptic('commit');
                seed(anchor, swingRadiusNm({ scopeM, depthM, bowHeightM }));
                useShellStore.getState().showToast('Back to the swing circle shape');
              }}
            >
              Reset to the circle
            </button>
            <button
              type="button"
              className="acp-btn"
              onClick={() => { haptic('tick'); clearZone(); setMode('radius'); }}
            >
              Clear
            </button>
          </div>
          <p className="acp-menu-note">
            {zone.length >= 3 ? `Boundary set with ${zone.length} points.` : 'No boundary shape yet.'}
          </p>
        </>
      )}
    </>
  );
}
